/**
 * CityAutocomplete — a searchable city picker (type ≥2 letters → dropdown of
 * "City, State, Country" candidates → SELECT auto-fills city/state/country +
 * coordinates in one action).
 *
 * REUSE, not reinvent:
 *   · Interaction + a11y model (role="combobox"/listbox/option, arrow-key +
 *     Enter nav, mousedown-before-blur, blur-close timer) is lifted from the
 *     intake `Combobox` (src/components/IntakeInputs.tsx). The difference: the
 *     intake Combobox filters a STATIC option list; this one runs a debounced
 *     LIVE search with a stale-result seq guard — the exact pattern the astro
 *     portal's CityAutocomplete uses (av-astro/frontend/src/components/
 *     CityAutocomplete.vue).
 *   · The search itself is `geocodeSearch` (src/lib/geocoder.ts) — the live
 *     typeahead runs on Photon (Nominatim powers only the single save-time
 *     lookup), now returning multiple structured hits instead of one best guess.
 *
 * The typed value IS the field value (free text is never lost) — so manual
 * typing without selecting still works, and the caller keeps its blur-geocode
 * fallback. Selecting a hit just fills the structured fields via `onSelect`.
 */
import { useId, useRef, useState } from 'react';
import { geocodeSearch, type CitySearchHit } from '../lib/geocoder';

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

const ddStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 2px)',
  left: 0,
  right: 0,
  background: 'var(--surface)',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  boxShadow: '0 8px 22px rgba(0,0,0,.12)',
  zIndex: 40,
  // No inner scroll: the list is capped at ≤6 short rows, so the dropdown grows
  // to fit them all. An inner scrollbar-within-a-scroll is banned house-wide
  // ([[feedback-no-nested-scrollbars]] / Vera LOW-4); the page owns the only
  // scroll. `visible` lets it overlay content normally (it is absolute).
  overflow: 'visible',
};

export interface CityAutocompleteProps {
  /** The city string (the field value; free text is preserved). */
  value: string;
  /** Fired on every keystroke — keeps the city input controlled. */
  onChange: (v: string) => void;
  /** Fired when the user PICKS a candidate — fill state/country/coords here. */
  onSelect: (hit: CitySearchHit) => void;
  placeholder?: string;
  ariaLabel?: string;
  /** Min chars before a search fires (default 2). */
  minChars?: number;
  /** Debounce window in ms (default 350). */
  debounceMs?: number;
  /** Max candidates to request/show (default 6). */
  limit?: number;
  disabled?: boolean;
}

export function CityAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'City — type to search',
  ariaLabel = 'Birth city',
  minChars = 2,
  debounceMs = 350,
  limit = 6,
  disabled,
}: CityAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState<CitySearchHit[]>([]);
  const [active, setActive] = useState(-1);
  const [searched, setSearched] = useState('');
  const listId = useId();
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  function runSearch(q: string) {
    const query = q.trim();
    setActive(-1);
    if (query.length < minChars) {
      setHits([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    setLoading(true);
    setOpen(true);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      const mySeq = ++seq.current;
      void geocodeSearch(query, limit)
        .then((results) => {
          if (mySeq !== seq.current) return; // a newer search superseded this
          setHits(results);
          setSearched(query);
          setOpen(true);
        })
        .catch(() => {
          if (mySeq === seq.current) setHits([]);
        })
        .finally(() => {
          if (mySeq === seq.current) setLoading(false);
        });
    }, debounceMs);
  }

  function pick(hit: CitySearchHit) {
    onSelect(hit);
    setOpen(false);
    setActive(-1);
    setHits([]);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      if (hits.length) setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && active >= 0 && hits[active]) {
        e.preventDefault();
        pick(hits[active]);
      }
    } else if (e.key === 'Tab') {
      // Parity with common comboboxes: Tab accepts the highlighted option and
      // lets focus advance naturally (no preventDefault). Plain typing +
      // blur-geocode still works when nothing is highlighted.
      if (open && active >= 0 && hits[active]) {
        pick(hits[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  }

  const labelFor = (h: CitySearchHit) =>
    [h.city, h.state, h.country].filter(Boolean).join(', ') || h.display_name;

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-opt-${active}` : undefined}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          runSearch(e.target.value);
        }}
        onFocus={() => {
          if (hits.length && value.trim().length >= minChars) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 160);
        }}
      />
      {open && (loading || hits.length > 0 || searched) && (
        <div style={ddStyle}>
          {/* Status lives OUTSIDE the listbox: a listbox may only contain
              options. This sibling aria-live region announces search progress /
              empty results to screen readers. */}
          {(loading || (hits.length === 0 && searched)) && (
            <div
              role="status"
              aria-live="polite"
              style={{ padding: '8px 11px', fontSize: 13, color: 'var(--ink-mute)' }}
            >
              {loading
                ? 'Searching…'
                : 'No matches — refine, or type the city and add state / country.'}
            </div>
          )}
          <div id={listId} role="listbox">
            {!loading &&
            hits.map((h, i) => (
              <div
                key={`${h.lat},${h.lon},${i}`}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  // mousedown (not click) so it fires before the input blur closes the list
                  e.preventDefault();
                  if (blurTimer.current) clearTimeout(blurTimer.current);
                  pick(h);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  // 3px solid accent bar (kept transparent when inactive so text
                  // never shifts) — the portal's house pattern for a selected list
                  // row. Reads clearly on-phone where the wash alone (1.10:1) did
                  // not; text contrast on --accent-wash stays 15.76:1.
                  borderLeft:
                    i === active
                      ? '3px solid var(--accent)'
                      : '3px solid transparent',
                  padding: '8px 11px',
                  fontSize: 13,
                  color: 'var(--ink)',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--rule)',
                  background: i === active ? 'var(--accent-wash)' : 'transparent',
                  lineHeight: 1.35,
                }}
              >
                {labelFor(h)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
